/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useFetch, useApiClient } from '@marageti/z4-lib';
import { TripRequest, TripRequestStatus } from '@marageti/z4-sdk/lib/travel';
import { Traveler, TravelerProfile } from '@marageti/z4-sdk/lib/people';
import { useSnackbar } from 'notistack';
import { useConfirm } from 'material-ui-confirm';
import { User, UserStatus } from '@marageti/z4-sdk/lib/auth';
import TripRequestView from './trip-request-view';
import { useAuthStore } from '../../store';
import useTravelerTicketsCount from '../../hooks/use-traveler-tickets-count';

const TripRequestContainer = () => {
  const apiClient = useApiClient();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const { tripRequestId } = useParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [isReenabling, setIsReenabling] = useState(false);
  const [isSwitchingVisability, setIsSwitchingVisability] = useState(false);
  const [isDetailsVisible, setIsDetailsVisible] = useState(true);
  const [isReviewVisible, setIsReviewVisible] = useState(true);

  const {
    data: tripRequestData,
    isLoading: isLoadingTripRequest,
    mutate,
  } = useFetch<TripRequest>(
    apiClient.tripRequestsClient.getTripRequest,
    [tripRequestId],
    !!tripRequestId,
  );
  const travelerId = tripRequestData?.travelers[0].id;
  const travelerProfile = useFetch<TravelerProfile>(apiClient.peopleClient.getTravelerProfile, [travelerId], !!travelerId);
  const { mutate: refetchTicketCount } = useTravelerTicketsCount(travelerId);
  const { data: primaryTraveler } = useFetch<Traveler>(apiClient.peopleClient.getTraveler, [travelerId], !!travelerId);
  const { loggedInTeamMember } = useAuthStore();
  const travelers = primaryTraveler && tripRequestData?.travelers && tripRequestData.travelers.length > 0
    ? [primaryTraveler, ...tripRequestData.travelers.slice(1)]
    : tripRequestData?.travelers;
  const loading = !tripRequestData || isLoadingTripRequest;

  useEffect(() => {
    if (tripRequestData) {
      setIsDetailsVisible(!tripRequestData.visibilityFlags.hideDetails);
      setIsReviewVisible(!tripRequestData.visibilityFlags.hideReviewRequest);
    }
  }, [tripRequestData]);

  const verifyTripRequest = async () => {
    try {
      if (tripRequestId) {
        setIsVerifying(true);
        await apiClient.tripRequestsClient.putTripRequestVerifications(tripRequestId);
        const verifyingKey = enqueueSnackbar('Verifying Traveler....', { variant: 'default' });
        const pollInterval = setInterval(async () => {
          const updatedTripRequest = await apiClient.tripRequestsClient.getTripRequest(tripRequestId);
          if (updatedTripRequest.status === TripRequestStatus.VerifiedStatus) {
            mutate();
            clearInterval(pollInterval);
            setIsVerifying(false);
            closeSnackbar(verifyingKey);
            enqueueSnackbar('Traveler verified.', { variant: 'success' });
          }
        }, 500); // Poll every 500ms seconds
      }
    } catch (error) {
      setIsVerifying(false);
    }
  };

  const confirmVerify = () => {
    confirm({
      title: 'Verify Traveler?',
      description: 'Changing this traveler to verified will allow you to match or take other actions on this trip request.',
      confirmationButtonProps: {
        color: 'primary',
      },
      confirmationText: 'Verify',
    })
      .then(verifyTripRequest)
      .catch(() => {});
  };

  const handleReactivation = async () => {
    if (tripRequestId && tripRequestData) {
      try {
        setIsReactivating(true);
        await apiClient.tripRequestsClient.putTripRequestStatus(tripRequestId, {
          id: tripRequestId,
          status: tripRequestData.statusInfo.lastStatus as TripRequestStatus,
          statusInfo: {
            reason: 'Agent Reactivated Trip',
          },
        });
        mutate();
        enqueueSnackbar('Trip reactivated.', { variant: 'success' });
      } catch (error) {
        console.error(error);
      } finally {
        setIsReactivating(false);
      }
    }
  };

  const handleReenable = async () => {
    if (tripRequestData?.travelers[0]?.userInfo[0]) {
      setIsReenabling(true);
      try {
        const { id } = tripRequestData.travelers[0].userInfo[0];
        const user: User = await apiClient.authClient.getUser(id);
        const updatedUser: User = {
          ...user,
          status: UserStatus.ENABLED,
        };

        await apiClient.authClient.putUser(user.id, updatedUser);
        await travelerProfile.mutate();
        mutate();
        enqueueSnackbar('Traveler reenabled.', { variant: 'success' });
      } catch (error) {
        console.error(error);
        enqueueSnackbar('Error reenabling traveler.', { variant: 'default' });
      } finally {
        setIsReenabling(false);
      }
    }
  };

  const onClickJunk = () => {
    const isJunk = tripRequestData?.status === TripRequestStatus.JunkStatus;
    const description = isJunk
      ? 'This will remove this request from Junk.'
      : <>Continuing with this action means Zicasso will never service this trip. It will still be available in the <em>All</em> queue.</>;
    confirm({
      title: isJunk ? 'Not Junk' : 'Junk Trip?',
      description,
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
    })
      .then(() => apiClient.tripRequestsClient.putTripRequestStatus(tripRequestId!, {
        id: tripRequestId!,
        status: isJunk ? TripRequestStatus.VerifiedStatus : TripRequestStatus.JunkStatus,
      }))
      .then(() => {
        mutate();
        enqueueSnackbar(isJunk ? 'Removed this request from Junk.' : 'Trip junked.', { variant: 'success' });
      })
      .catch((e: ApiError) => {
        if (e !== undefined) enqueueSnackbar('There was an error junking this request.', { variant: 'default' });
      });
  };

  const handleSwitchVisability = async (event: React.ChangeEvent<HTMLInputElement>, type: 'details' | 'review') => {
    if (!tripRequestData) return;
    if (type === 'details') {
      setIsDetailsVisible(event.target.checked);
    }
    if (type === 'review') {
      setIsReviewVisible(event.target.checked);
    }
    try {
      setIsSwitchingVisability(true);
      await apiClient.tripRequestsClient.putTripRequestVisibilityFlags(tripRequestId!, {
        id: tripRequestId!,
        visibilityFlags: {
          hideDetails: type === 'details' ? !event.target.checked : tripRequestData?.visibilityFlags.hideDetails as boolean,
          hideReviewRequest: type === 'review' ? !event.target.checked : tripRequestData?.visibilityFlags.hideReviewRequest as boolean,
        },
      });
      mutate();
      enqueueSnackbar('View settings updated.', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('View settings failed to update', { variant: 'default' });
    } finally {
      setIsSwitchingVisability(false);
    }
  };

  return (
    <TripRequestView
      handleSwitchVisability={handleSwitchVisability}
      handleReactivation={handleReactivation}
      handleReenable={handleReenable}
      handleVerify={confirmVerify}
      isDetailsVisible={isDetailsVisible}
      isReviewVisible={isReviewVisible}
      isReactivating={isReactivating}
      isReenabling={isReenabling}
      isSwitchingVisability={isSwitchingVisability}
      isVerifying={isVerifying}
      loading={loading}
      loggedInTeamMember={loggedInTeamMember}
      onClickJunk={onClickJunk}
      travelers={travelers || []}
      travelerId={travelerId}
      tripRequest={tripRequestData}
      onTravelerEdit={() => {
        travelerProfile.mutate();
        mutate();
      }}
      mutate={mutate}
      refetchTicketCount={refetchTicketCount}
    />
  );
};

export default TripRequestContainer;
